
// Sentiment based on NLP comment scores

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import { Typography, CircularProgress } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

import backgroundColorPlugin from "../../plugin/backgroundColorPlugin";
// import { generateColors } from "utils/chartColors";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import EntitiesPopup from "layouts/components/EntitiesPopup";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

function AudienceSentimentTimelineChart({
  title,
  datasets = [],
  previousdatasets = [],
  futuredatasets = [],
  isPredictionEnabled = false,
  isLoading = false,
  isEntitled,
  featureName,
  planRequired,
  entitlements // ✅ and entitlements
}) {
  // ----------------------------------------------------------------------
  // SAFE DATASET NORMALISATION (prevents undefined errors)
  // ----------------------------------------------------------------------
  const safeDatasets = Array.isArray(datasets) ? datasets : [];

  // ----------------------------------------------------------------------
  // POPUP HOOKS — MUST ALWAYS RUN
  // ----------------------------------------------------------------------
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupDate, setPopupDate] = useState(null);

  const handlePointClick = (date) => {
    setPopupDate(date);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupDate(null);
  };

  const getDailySentiment = (entries, platform) => {
    const dailyScores = {};

    // Step 1 — gather all unique dates
    const allDates = [
      ...new Set(
        entries.map((entry) => {
          const dt = new Date(entry.date || entry.datetime);
          return isNaN(dt) ? null : dt.toISOString().split("T")[0];
        })
      ),
    ].filter(Boolean).sort();

    // Step 2 — initialize each date with an empty array (NO 50 here)
    allDates.forEach((d) => (dailyScores[d] = []));

    // Step 3 — accumulate all comment zoneScores for each date
    entries.forEach((entry) => {
      const dt = new Date(entry.date || entry.datetime);
      if (isNaN(dt)) return;

      const date = dt.toISOString().split("T")[0];
      const posts = platform === "YouTube" ? entry.videos : entry.posts || [];

      posts.forEach((post) => {
        const comments = post.comments || [];

        comments.forEach((c) => {
          const score = c.sentiment?.zoneScore;
          if (typeof score === "number") {
            dailyScores[date].push(score);
          }
        });
      });
    });

    // Step 4 — if a date has ZERO comments, assign a single fallback 50
    Object.keys(dailyScores).forEach((date) => {
      if (dailyScores[date].length === 0) {
        dailyScores[date] = [50]; // single fallback per day
      }
    });

    // Step 5 — compute averages
    return Object.entries(dailyScores).map(([date, scores]) => ({
      date,
      avgScore: scores.reduce((a, b) => a + b, 0) / scores.length,
    }));
  };


  // ----------------------------------------------------------------------
  // CHART DATA (useMemo for performance)
  // ----------------------------------------------------------------------
  const chartData = useMemo(() => {
    const allDatesSet = new Set();
    // const colors = generateColors(safeDatasets.length, profileIds);

    const processedData = safeDatasets.map((profile) => {
      const values = getDailySentiment(profile.data || [], profile.platform);
      values.forEach((item) => allDatesSet.add(item.date));
      return values;
    });

    const labels = Array.from(allDatesSet).sort();

    const formattedDatasets = processedData.map((values, idx) => {
      const map = Object.fromEntries(values.map((v) => [v.date, v.avgScore]));
      const data = labels.map((date) => map[date] ?? null);

      return {
        label: safeDatasets[idx].name || safeDatasets[idx].label || "Profile",
        data,
        profilePictureUrl: safeDatasets[idx].profilePictureUrl,
        platform: safeDatasets[idx].platform,
        profileId: safeDatasets[idx].profileId, // ✅ Pass ID for DefaultLineChart to generate color
        metricName: "Sentiment Score", // ✅ Explicitly set metric name
        fill: false,
        tension: 0.3,
      };
    });

    // FIX 1: Hardcode min/max for the 0-100 scale.
    const fixedMin = 0;
    const fixedMax = 100;
    const buffer = (fixedMax - fixedMin) * 0.01; // Small buffer for visual comfort (1%)

    return {
      labels,
      datasets: formattedDatasets,
      yAxisMin: fixedMin - buffer, // Use fixed 0-100 range
      yAxisMax: fixedMax + buffer, // Use fixed 0-100 range
      backgroundColorPluginOptions: {
        regions: [
          { color: "rgba(75,192,192,0.2)", start: 60, end: fixedMax + buffer }, // Positive (60-100)
          { color: "rgba(255,205,86,0.2)", start: 40, end: 60 }, // Neutral (40-60)
          { color: "rgba(255,99,132,0.2)", start: fixedMin - buffer, end: 40 }, // Negative (0-40)
        ],
      },
    };
  }, [safeDatasets]);

  // Check if all data points are the default 50 (indicating no real sentiment data)
  const isAllDefault = useMemo(() => {
    if (!chartData?.datasets?.length) return true;
    return chartData.datasets.every(ds =>
      ds.data.every(val => val === 50 || val === null)
    );
  }, [chartData]);

  // Override metrics values if data is all default
  // Override metrics values to use LAST value instead of aggregation
  const customMetrics = useMemo(() => {
    // If no data, return N/A
    if (!safeDatasets || safeDatasets.length === 0) return [];

    return safeDatasets.map((profile) => {
      // 1. Current Period Last Value
      const currentValues = getDailySentiment(profile.data || [], profile.platform);
      // Sort by date ascending to ensure last item is latest
      currentValues.sort((a, b) => new Date(a.date) - new Date(b.date));
      const lastCurrent = currentValues.length > 0 ? currentValues[currentValues.length - 1].avgScore : 0;

      // 2. Previous Period Last Value
      let lastPrevious = 0;
      if (previousdatasets) {
        const prevProfile = previousdatasets.find(p => p.profileId === profile.profileId);
        if (prevProfile) {
          const prevValues = getDailySentiment(prevProfile.data || [], prevProfile.platform);
          prevValues.sort((a, b) => new Date(a.date) - new Date(b.date));
          if (prevValues.length > 0) {
            lastPrevious = prevValues[prevValues.length - 1].avgScore;
          }
        }
      }

      return {
        metricName: "Sentiment Score",
        // If all data is default (empty), show N/A
        currentMetricValue: isAllDefault ? "N/A" : lastCurrent,
        previousMetricValue: isAllDefault ? "N/A" : lastPrevious,
      };
    });
  }, [safeDatasets, previousdatasets, isAllDefault]);

  // ----------------------------------------------------------------------
  // Y-AXIS LABEL OPTIONS (FIX 2: Custom callback for mid-zone labels)
  // ----------------------------------------------------------------------
  const yAxisOptions = {
    min: chartData.yAxisMin,
    max: chartData.yAxisMax,
    ticks: {
      stepSize: 10,
      autoSkip: false, // Ensure all ticks are checked for labels
      callback: (value) => {
        // Calculate the mid-points for each zone:
        // Negative Zone Midpoint: (0 + 40) / 2 = 20
        // Neutral Zone Midpoint: 50
        // Positive Zone Midpoint: (60 + 100) / 2 = 80

        if (value === 80) return "Positive";
        if (value === 50) return "Neutral";
        if (value === 20) return "Negative";

        // Return an empty string for all other ticks (10, 30, 40, 60, 70, 90, 100)
        return "";
      },
    },
  };

  const plugins = [backgroundColorPlugin];

  // ----------------------------------------------------------------------
  // TOOLTIP CONTENT
  // ----------------------------------------------------------------------
  // ----------------------------------------------------------------------
  // TOOLTIP CONTENT
  // ----------------------------------------------------------------------
  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart shows the daily trend of audience sentiment based on NLP analysis
        of comments (0–100 scale).
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - <strong>60–100 (Green)</strong>: Positive sentiment.<br />
        - <strong>40–60 (Yellow)</strong>: Neutral sentiment.<br />
        - <strong>0–40 (Red)</strong>: Negative sentiment.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Identify spikes in negative feedback to address issues quickly. <br />
        - Correlate positive sentiment with specific content or announcements. <br />
        - Click a point to view specific comments driving the score.
      </Typography>
    </div>
  );

  // ----------------------------------------------------------------------
  // FINAL RENDER (NO early return)
  // ----------------------------------------------------------------------
  return (
    <>
      {isLoading ? (
        <Card>
          <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
            <CircularProgress size={32} thickness={4} color="accent" />
          </MDBox>
        </Card>
      ) : (
        <DefaultLineChart
          title={title}
          description={tooltipContent}
          height="19.125rem"
          chart={{
            labels: chartData.labels,
            datasets: chartData.datasets,
          }}
          plugins={plugins}
          yAxisTicks={yAxisOptions} // Passing fixed options
          backgroundColorPluginOptions={chartData.backgroundColorPluginOptions}
          is_derived={true}
          onPointClick={handlePointClick}
          isEntitled={isEntitled}
          featureName={featureName}
          planRequired={planRequired}
          isEmpty={isAllDefault} // ✅ Pass empty state
          metrics={customMetrics} // ✅ Pass custom metrics
          rawData={safeDatasets}
          entitlements={entitlements} // ✅ Pass entitlements
          disableToggle={true} // ✅ Disable toggle for this chart
        />
      )}

      {isPopupOpen && (
        <EntitiesPopup
          isModalOpen={isPopupOpen}
          onClose={handlePopupClose}
          datasets={safeDatasets}
          date={popupDate}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------------------
// PROP TYPES
// ----------------------------------------------------------------------
AudienceSentimentTimelineChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.array,
  previousdatasets: PropTypes.array,
  futuredatasets: PropTypes.array,
  isPredictionEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  entitlements: PropTypes.object,
};

export default AudienceSentimentTimelineChart;